import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Minus, ArrowRight, Sparkles, CheckCircle2 } from "lucide-react";
import { api, pick } from "../lib/api";
import { useLang } from "../lib/i18n";
import { Reveal, SectionTitle, FadeIn } from "../components/Reveal";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";

const fmt = (n) => `Rs ${Number(n).toLocaleString("en-MU")}`;

export default function Packages() {
  const { t, lang } = useLang();
  const { data: services = [] } = useQuery({ queryKey: ["services"], queryFn: () => api.get("/services").then(r => r.data) });

  const included = [
    { title: t.compare.full, desc: t.compare.fullDesc, dark: true, items: [t.compare.inc1, t.compare.inc1f, t.compare.inc2] },
    { title: t.compare.half, desc: t.compare.halfDesc, dark: false, items: [t.compare.inc1, t.compare.inc1h, t.compare.inc2] },
  ];

  return (
    <div className="pt-[72px]" data-testid="packages-page">
      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-14 md:pt-20">
        <SectionTitle eyebrow={t.compare.eyebrow} title={t.compare.title} sub={t.compare.sub} />
        <div className="mt-12 grid md:grid-cols-2 gap-6">
          {included.map((c, i) => (
            <Reveal key={i} delay={0.08 * i}>
              <div className={`rounded-[28px] p-9 h-full ${c.dark ? "bg-forest-700 text-white shadow-xl shadow-forest-700/25" : "bg-white border border-forest-100"}`} data-testid={`packages-included-${i}`}>
                <div className="flex items-center gap-2.5">
                  {c.dark ? <Sparkles size={19} className="text-forest-200" /> : <CheckCircle2 size={19} className="text-forest-500" />}
                  <h3 className="font-display text-2xl font-semibold tracking-tight">{c.title}</h3>
                </div>
                <p className={`mt-2 text-sm ${c.dark ? "text-forest-100/90" : "text-muted-foreground"}`}>{c.desc}</p>
                <ul className="mt-6 space-y-3">
                  {c.items.map((f, j) => (
                    <li key={j} className={`flex items-start gap-2.5 text-sm font-medium ${c.dark ? "text-forest-50" : "text-forest-900"}`}>
                      {j === 1 && !c.dark ? <Minus size={17} className="mt-0.5 shrink-0 text-muted-foreground" /> : <Check size={17} className={`mt-0.5 shrink-0 ${c.dark ? "text-forest-200" : "text-forest-500"}`} strokeWidth={3} />} {f}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-10 py-16 md:py-24" data-testid="packages-table-section">
        <Reveal>
          <div className="rounded-[28px] border border-forest-100 bg-white shadow-sm overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="bg-mist hover:bg-mist">
                  <TableHead className="text-forest-900 font-display font-semibold text-base py-5 pl-7">{t.admin.col.service}</TableHead>
                  <TableHead className="text-forest-700 font-display font-semibold text-base text-center">{t.compare.full}</TableHead>
                  <TableHead className="text-forest-700 font-display font-semibold text-base text-center">{t.compare.half}</TableHead>
                  <TableHead className="pr-7" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {services.map((s) => (
                  <TableRow key={s.slug} className="hover:bg-mint/40" data-testid={`packages-row-${s.slug}`}>
                    <TableCell className="py-5 pl-7">
                      <Link to={`/services/${s.slug}`} className="group" data-testid={`packages-link-${s.slug}`}>
                        <div className="font-display font-semibold text-forest-900 group-hover:text-forest-600 transition-colors">{pick(s, "name", lang)}</div>
                        <div className="text-xs text-muted-foreground mt-0.5">{pick(s, "unit", lang)}</div>
                      </Link>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center rounded-full bg-forest-700 text-white px-4 py-1.5 font-display font-bold text-sm">{fmt(s.full_price)}</span>
                    </TableCell>
                    <TableCell className="text-center">
                      <span className="inline-flex items-center rounded-full border border-forest-200 text-forest-800 px-4 py-1.5 font-display font-bold text-sm">{fmt(s.half_price)}</span>
                    </TableCell>
                    <TableCell className="pr-7 text-right">
                      <Link to={`/booking?service=${s.slug}`} data-testid={`packages-book-${s.slug}`} className="group inline-flex items-center gap-1.5 text-sm font-semibold text-forest-700 hover:text-forest-900 transition-colors whitespace-nowrap">
                        {t.common.book} <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </Reveal>
        <FadeIn delay={0.1}>
          <p className="mt-6 text-sm text-muted-foreground" data-testid="packages-note">
            {lang === "fr"
              ? "Prix indicatifs pour un travail standard — demandez un devis gratuit pour tout besoin sur mesure."
              : "Prices are indicative for standard jobs — request a free quote for anything custom."}
          </p>
        </FadeIn>
      </section>
    </div>
  );
}